import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { randomString, supportsInlineSVG, uniquifySVGIDs } from './utils'

import s from './InlineSvg.scss'


const Status = {
  PENDING: 'pending',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
  UNSUPPORTED: 'unsupported',
}

const getRequestsByUrl = {}
const loadedFiles = {}

class InlineSvg extends PureComponent {

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    preloader: PropTypes.node,
    src: PropTypes.string.isRequired,
    cache: PropTypes.bool,
    uniquifyIDs: PropTypes.bool,
    uniqueHash: PropTypes.string,
    tag: PropTypes.string,
    processSVG: PropTypes.func,
  }

  static defaultProps = {
    cache: false,
    uniquifyIDs: true,
    tag: 'div',
  }

  state = {
    status: Status.PENDING,
    loadedText: null,
  }

  isActive = false

  componentWillMount() {
    this.isActive = true
  }

  componentDidMount() {
    const { src } = this.props
    const { status } = this.state

    if (status === Status.PENDING) {
      if (supportsInlineSVG()) {
        if (src) {
          this.startLoad()
          return
        }

        this.fail('InlineSVG: Empty src')
        return
      }

      this.fail('InlineSVG: Unsupported browser')
    }
  }

  componentDidUpdate(prevProps) {
    const { src } = this.props

    if (prevProps.src !== src) {
      if (src) {
        this.startLoad()
        return
      }

      this.fail('Missing source')
    }
  }

  componentWillUnmount() {
    this.isActive = true
  }

  fail(message) {
    console.warn(message)

    if (this.isActive) {
      this.setState({
        status: Status.FAILED,
      })
    }
  }

  startLoad() {
    if (this.isActive) {
      this.setState({
        status: Status.LOADING,
      }, this.load)
    }
  }

  request(src, cb) {
    const xhr = new XMLHttpRequest()

    xhr.onload = () => {
      if (xhr.status !== 200) {
        cb(`InlineSvg: Request failed: ${src}, status: ${xhr.status}`)
      }
      else if (xhr.response === null) {
        cb(`InlineSvg: Empty response: ${src}`)
      }
      else {
        cb(null, xhr.response)
      }
    }

    xhr.onerror = () => {
      cb(`InlineSvg: Request failed: ${src}`)
    }

    xhr.open('GET', src, true)
    xhr.responseType = 'text'
    xhr.send()
  }

  load() {
    const { src, cache } = this.props

    if (cache) {
      if (loadedFiles[src]) {
        const [err, res] = loadedFiles[src]

        this.handleLoad(err, res)
        return
      }

      if (!getRequestsByUrl[src]) {
        getRequestsByUrl[src] = [this.handleLoad]

        this.request(src, (err, res) => {
          getRequestsByUrl[src].forEach((cb) => {
            const { src: currentSrc } = this.props
            loadedFiles[src] = [err, res]

            if (src === currentSrc) {
              cb(err, res)
            }
          })

          delete getRequestsByUrl[src]
        })
      }
      else {
        getRequestsByUrl[src].push(this.handleLoad)
      }

      return
    }

    this.request(src, (err, res) => {
      const { src: currentSrc } = this.props

      if (currentSrc === src) {
        this.handleLoad(err, res)
      }
    })
  }

  handleLoad = (err, res) => {
    if (err) {
      this.fail(err)
      return
    }

    if (this.isActive) {
      this.setState({
        loadedText: res,
        status: Status.LOADED,
      })
    }
  }

  processSVG(text) {
    const {
      uniquifyIDs,
      uniqueHash,
      baseURL,
      processSVG,
    } = this.props

    let svgText = text
    if (processSVG) {
      svgText = processSVG(svgText)
    }

    // remove xml
    svgText = svgText.replace(/<\?xml(.*?)\?>/, '')

    if (uniquifyIDs) {
      return uniquifySVGIDs(svgText, uniqueHash || randomString(), baseURL)
    }

    return svgText
  }

  renderPreloader() {
    const { status } = this.state
    const { children, className, tag, preloader } = this.props

    let content = preloader

    if (status === Status.UNSUPPORTED || status === Status.FAILED) {
      content = children // fallback - use <img /> in children
    }

    return React.createElement(tag, { className: cx(s.svg, className) }, content)
  }

  renderSvg() {
    const { loadedText } = this.state
    const { className, tag } = this.props

    const html = this.processSVG(loadedText)

    return React.createElement(tag, {
      className: cx(s.svg, className),
      draggable: false,
      dangerouslySetInnerHTML: html ? { __html: html } : null,
    })
  }

  render() {
    const { loadedText } = this.state

    if (!loadedText) {
      return this.renderPreloader()
    }

    return this.renderSvg()
  }
}


export default InlineSvg
